<template>
  <input
    type="text"
    :id="id"
    :placeholder="placeholder"
    :value="displayValue"
    @input="onInput"
    @keydown.down.prevent="onArrowDown"
    @keydown.up.prevent="onArrowUp"
    @keydown.enter.prevent="onEnter"
    @blur="onBlur"
    class="pac-target-input"
    autocomplete="off"
  />
</template>

<script>
var { mapboxAccessToken } = require("../../config/config.js");

// Fallback if not defined in config
var MAPBOX_TOKEN = (typeof mapboxAccessToken !== 'undefined' && mapboxAccessToken)
  ? mapboxAccessToken
  : "YOUR_MAPBOX_ACCESS_TOKEN_HERE";

export default {
  name: "MapboxAutocomplete",
  props: {
    id: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: "Enter a location"
    },
    value: {
      type: [String, Object],
      default: ""
    }
  },
  data() {
    return {
      inputValue: "",
      suggestions: [],
      selectedIndex: -1,
      debounceTimer: null,
      dropdownEl: null,
      isSelecting: false
    };
  },
  computed: {
    displayValue() {
      if (typeof this.value === 'object' && this.value && this.value.formatted_address) {
        return this.value.formatted_address;
      }
      if (typeof this.value === 'string') {
        return this.value;
      }
      return this.inputValue;
    }
  },
  methods: {
    onInput(e) {
      var val = e.target.value;
      this.inputValue = val;
      this.$emit("inputChange", { newVal: val }, this.id);

      if (this.debounceTimer) clearTimeout(this.debounceTimer);

      if (!val || val.length < 3) {
        this.closeSuggestions();
        return;
      }

      this.debounceTimer = setTimeout(function () {
        this.fetchSuggestions(val);
      }.bind(this), 300);
    },

    fetchSuggestions(query) {
      var encodedQuery = encodeURIComponent(query);
      var url = "https://api.mapbox.com/geocoding/v5/mapbox.places/" + encodedQuery + ".json?access_token=" + MAPBOX_TOKEN + "&autocomplete=true&limit=5";

      var xhr = new XMLHttpRequest();
      xhr.open("GET", url);
      xhr.onload = function () {
        if (xhr.status === 200) {
          try {
            var data = JSON.parse(xhr.responseText);
            if (data.features && data.features.length > 0) {
              this.suggestions = data.features;
              this.selectedIndex = -1;
              this.showSuggestions();
            } else {
              this.closeSuggestions();
            }
          } catch (e) {
            this.$emit("error", "Failed to parse geocoding response");
            this.closeSuggestions();
          }
        } else {
          this.$emit("error", "Geocoding request failed with status " + xhr.status);
          this.closeSuggestions();
        }
      }.bind(this);
      xhr.onerror = function () {
        this.$emit("error", "Geocoding network request failed");
        this.closeSuggestions();
      }.bind(this);
      xhr.send();
    },

    removeDropdown() {
      if (this.dropdownEl && this.dropdownEl.parentNode) {
        this.dropdownEl.parentNode.removeChild(this.dropdownEl);
      }
      this.dropdownEl = null;
    },

    showSuggestions() {
      this.removeDropdown();

      var inputEl = this.$el;
      var rect = inputEl.getBoundingClientRect();

      var dropdown = document.createElement("div");
      dropdown.className = "mapbox-autocomplete-dropdown";
      dropdown.style.cssText = "position:fixed;z-index:9999;background:#fff;border:1px solid #ccc;border-top:none;max-height:200px;overflow-y:auto;box-shadow:0 2px 6px rgba(0,0,0,0.2);left:" + rect.left + "px;top:" + rect.bottom + "px;width:" + rect.width + "px;";

      for (var i = 0; i < this.suggestions.length; i++) {
        var item = document.createElement("div");
        item.className = "mapbox-autocomplete-item";
        item.style.cssText = "padding:8px 12px;cursor:pointer;font-size:14px;border-bottom:1px solid #eee;";
        item.textContent = this.suggestions[i].place_name;
        item.setAttribute("data-index", i);

        item.addEventListener("mousedown", function (e) {
          e.preventDefault();
        });
        item.addEventListener("click", function (e) {
          var idx = parseInt(e.currentTarget.getAttribute("data-index"));
          this.selectSuggestion(idx);
        }.bind(this));
        item.addEventListener("mouseenter", function (e) {
          var items = dropdown.querySelectorAll(".mapbox-autocomplete-item");
          for (var j = 0; j < items.length; j++) {
            items[j].style.backgroundColor = "#fff";
          }
          e.currentTarget.style.backgroundColor = "#f0f0f0";
        });
        item.addEventListener("mouseleave", function (e) {
          e.currentTarget.style.backgroundColor = "#fff";
        });

        dropdown.appendChild(item);
      }

      document.body.appendChild(dropdown);
      this.dropdownEl = dropdown;
    },

    closeSuggestions() {
      this.removeDropdown();
      this.suggestions = [];
      this.selectedIndex = -1;
    },

    selectSuggestion(index) {
      if (index < 0 || index >= this.suggestions.length) return;
      var feature = this.suggestions[index];

      var lng = feature.center[0];
      var lat = feature.center[1];

      // Build addressData compatible with vue-google-autocomplete format
      var addressData = {
        latitude: lat,
        longitude: lng
      };

      // Parse context for address components
      if (feature.context) {
        for (var i = 0; i < feature.context.length; i++) {
          var ctx = feature.context[i];
          if (ctx.id && ctx.id.indexOf("country") === 0) {
            addressData.country = ctx.text;
          } else if (ctx.id && ctx.id.indexOf("region") === 0) {
            addressData.administrative_area_level_1 = ctx.text;
          } else if (ctx.id && ctx.id.indexOf("place") === 0) {
            addressData.locality = ctx.text;
          } else if (ctx.id && ctx.id.indexOf("postcode") === 0) {
            addressData.postal_code = ctx.text;
          }
        }
      }

      // Build placeResultData compatible with vue-google-autocomplete format
      var placeResultData = {
        formatted_address: feature.place_name,
        name: feature.text,
        geometry: {
          location: {
            lat: function () { return lat; },
            lng: function () { return lng; }
          }
        }
      };

      this.inputValue = feature.place_name;
      this.$el.value = feature.place_name;
      this.isSelecting = true;
      this.closeSuggestions();

      this.$emit("placechanged", addressData, placeResultData, this.id);
    },

    onArrowDown() {
      if (this.suggestions.length === 0) return;
      this.selectedIndex = (this.selectedIndex + 1) % this.suggestions.length;
      this.highlightItem(this.selectedIndex);
    },

    onArrowUp() {
      if (this.suggestions.length === 0) return;
      this.selectedIndex = this.selectedIndex <= 0 ? this.suggestions.length - 1 : this.selectedIndex - 1;
      this.highlightItem(this.selectedIndex);
    },

    onEnter() {
      if (this.selectedIndex >= 0 && this.selectedIndex < this.suggestions.length) {
        this.selectSuggestion(this.selectedIndex);
      }
    },

    onBlur() {
      // Delay to allow click events on suggestions to fire
      setTimeout(function () {
        this.closeSuggestions();
      }.bind(this), 200);
    },

    highlightItem(index) {
      if (!this.dropdownEl) return;
      var items = this.dropdownEl.querySelectorAll(".mapbox-autocomplete-item");
      for (var i = 0; i < items.length; i++) {
        items[i].style.backgroundColor = i === index ? "#f0f0f0" : "#fff";
      }
    }
  },

  beforeDestroy() {
    this.closeSuggestions();
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }
};
</script>



// WEBPACK FOOTER //
// src/components/MapboxAutocomplete.vue